import React, { useEffect, useRef } from "react";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import {
  DASHBOARD_TABS,
  FIND_A_PRO_TAB,
  DashboardTab,
  DashboardTabId,
} from "../../dashboard/dashboardTabs";
import { DASH, cardSx } from "../../dashboard/tokens";

type Props = {
  active: DashboardTabId;
  onSelect: (id: DashboardTabId) => void;
  /** Small count/attention badges per tab (e.g. overdue maintenance). */
  badges?: Partial<Record<DashboardTabId, number>>;
};

export const SIDEBAR_TAGLINE = "A more valuable home starts with a better record.";

/**
 * Left sidebar of the property dashboard (October 2026, Task 2). Every tab is
 * listed for every viewer; what a tab shows depends on who's looking. On
 * phones it becomes a horizontal, scrollable strip that sticks to the top.
 */
const ProIcon = FIND_A_PRO_TAB.icon;

const DashboardSidebar: React.FC<Props> = ({ active, onSelect, badges = {} }) => {
  const stripRef = useRef<HTMLDivElement>(null);

  // Keep the active pill in view on the mobile strip.
  useEffect(() => {
    const strip = stripRef.current;
    const pill = strip?.querySelector<HTMLElement>(`[data-tab="${active}"]`);
    if (strip && pill && typeof pill.scrollIntoView === "function" && strip.scrollWidth > strip.clientWidth) {
      const left = pill.offsetLeft - (strip.clientWidth - pill.offsetWidth) / 2;
      strip.scrollTo?.({ left, behavior: "smooth" });
    }
  }, [active]);

  return (
    <>
      {/* Desktop / tablet: vertical card */}
      <Box
        component="nav"
        aria-label="Property dashboard"
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          ...cardSx,
          p: 3,
          position: "sticky",
          top: 24,
        }}
        data-testid="dashboard-sidebar"
      >
        <Stack spacing={1}>
          {DASHBOARD_TABS.map((tab) => (
            <SidebarItem
              key={tab.id}
              tab={tab}
              active={active === tab.id}
              badge={badges[tab.id]}
              onSelect={onSelect}
            />
          ))}
        </Stack>

        <Box sx={{ my: 3, borderTop: `1px solid ${DASH.border}` }} />

        <ButtonBase
          onClick={() => onSelect(FIND_A_PRO_TAB.id)}
          aria-current={active === FIND_A_PRO_TAB.id ? "page" : undefined}
          sx={{
            justifyContent: "flex-start",
            textAlign: "left",
            borderRadius: "12px",
            p: 3,
            bgcolor: active === FIND_A_PRO_TAB.id ? DASH.teal : DASH.amberTint,
            color: active === FIND_A_PRO_TAB.id ? "#fff" : "#5b3a00",
            transition: "background-color .15s",
            "&:hover": { bgcolor: active === FIND_A_PRO_TAB.id ? DASH.tealDark : "rgba(251,190,104,0.36)" },
          }}
          data-testid={`sidebar-item-${FIND_A_PRO_TAB.id}`}
        >
          <ProIcon sx={{ mr: 2.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { md: 13.5, lg: 15 }, whiteSpace: "nowrap" }}>
              {FIND_A_PRO_TAB.label}
            </Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.85 }}>Free help from trusted local pros</Typography>
          </Box>
          <ChevronRight fontSize="small" />
        </ButtonBase>

        <Typography
          sx={{
            mt: 5,
            mb: 1,
            px: 2,
            fontSize: 13,
            fontStyle: "italic",
            lineHeight: 1.5,
            color: DASH.muted,
          }}
          data-testid="sidebar-tagline"
        >
          {SIDEBAR_TAGLINE}
        </Typography>
      </Box>

      {/* Phone: sticky horizontal strip */}
      <Box
        component="nav"
        aria-label="Property dashboard"
        ref={stripRef}
        sx={{
          display: { xs: "flex", md: "none" },
          gap: 2,
          overflowX: "auto",
          position: "sticky",
          top: 0,
          zIndex: 5,
          bgcolor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(6px)",
          // Bleed to the screen edges: the page gutter on phones is 12px
          // (8px container + 4px wrapper), so match it exactly — any more
          // and the strip makes the whole page scroll sideways.
          mx: -3,
          px: 3,
          py: 3,
          borderBottom: `1px solid ${DASH.border}`,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
        data-testid="dashboard-strip"
      >
        {[...DASHBOARD_TABS, FIND_A_PRO_TAB].map((tab) => {
          const isActive = active === tab.id;
          const isPro = tab.id === FIND_A_PRO_TAB.id;
          const Icon = tab.icon;
          return (
            <ButtonBase
              key={tab.id}
              data-tab={tab.id}
              onClick={() => onSelect(tab.id)}
              aria-current={isActive ? "page" : undefined}
              sx={{
                flexShrink: 0,
                borderRadius: 50,
                px: 3.5,
                py: 2,
                gap: 1.5,
                fontSize: 14,
                fontWeight: 700,
                whiteSpace: "nowrap",
                border: `1px solid ${isActive ? DASH.teal : isPro ? "rgba(251,190,104,0.9)" : DASH.border}`,
                bgcolor: isActive ? DASH.teal : isPro ? DASH.amberTint : "#fff",
                color: isActive ? "#fff" : isPro ? "#5b3a00" : DASH.teal,
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
              {tab.label}
              {!!badges[tab.id] && <CountBadge n={badges[tab.id]!} inverted={isActive} />}
            </ButtonBase>
          );
        })}
      </Box>
    </>
  );
};

const SidebarItem: React.FC<{
  tab: DashboardTab;
  active: boolean;
  badge?: number;
  onSelect: (id: DashboardTabId) => void;
}> = ({ tab, active, badge, onSelect }) => {
  const Icon = tab.icon;
  return (
  <ButtonBase
    onClick={() => onSelect(tab.id)}
    aria-current={active ? "page" : undefined}
    sx={{
      position: "relative",
      justifyContent: "flex-start",
      borderRadius: "10px",
      px: 3,
      py: 2.5,
      gap: 2.5,
      color: active ? DASH.teal : "#3a4646",
      bgcolor: active ? DASH.tealTint2 : "transparent",
      fontWeight: active ? 800 : 600,
      fontSize: 15,
      transition: "background-color .15s, color .15s",
      "&:hover": { bgcolor: active ? DASH.tealTint2 : DASH.tealTint },
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 8,
        bottom: 8,
        width: 3,
        borderRadius: 3,
        bgcolor: active ? DASH.teal : "transparent",
      },
    }}
    data-testid={`sidebar-item-${tab.id}`}
  >
    <Icon sx={{ fontSize: 21, color: active ? DASH.teal : "rgba(0,75,76,0.7)" }} />
    <Box component="span" sx={{ flex: 1, textAlign: "left" }}>
      {tab.label}
    </Box>
    {!!badge && <CountBadge n={badge} />}
  </ButtonBase>
  );
};

const CountBadge: React.FC<{ n: number; inverted?: boolean }> = ({ n, inverted }) => (
  <Box
    component="span"
    aria-label={`${n} need attention`}
    sx={{
      minWidth: 20,
      height: 20,
      px: 1.5,
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 800,
      lineHeight: "20px",
      textAlign: "center",
      bgcolor: inverted ? "#fff" : DASH.red,
      color: inverted ? DASH.red : "#fff",
    }}
  >
    {n > 9 ? "9+" : n}
  </Box>
);

export default DashboardSidebar;
